import { Type } from "@sinclair/typebox";
import { WorkspaceReadinessConfigurationSchema } from "./readiness.js";
import { ApprovalStateSchema, ListCursorSchema, RunResponseSchema } from "./runs.js";
import { IsoDateTimeSchema, JsonObjectSchema, OpaqueIdSchema, Sha256Schema, SubjectIdSchema } from "./primitives.js";
// Read-only workspace records behind the console's Approval, Operators, Connections, Audit and Settings pages.
// Every response is closed (additionalProperties: false), so the serializer drops any column a port might add.
export const WorkspaceRoleSchema = Type.Union([
    Type.Literal("owner"), Type.Literal("admin"), Type.Literal("operator"),
    Type.Literal("approver"), Type.Literal("auditor"), Type.Literal("viewer"),
]);
export const ApprovalDecisionRecordSchema = Type.Object({
    decided_by: SubjectIdSchema,
    decision: Type.Union([Type.Literal("approve"), Type.Literal("deny")]),
    reason: Type.Optional(Type.String({ minLength: 1, maxLength: 1_000 })),
    decided_at: IsoDateTimeSchema,
    decision_expires_at: IsoDateTimeSchema,
    decision_sha256: Sha256Schema,
}, { additionalProperties: false });
export const ApprovalDetailSchema = Type.Object({
    schema: Type.Literal("carbon.operations.approval/v1"),
    approval_id: OpaqueIdSchema,
    run_id: OpaqueIdSchema,
    workspace_id: OpaqueIdSchema,
    state: ApprovalStateSchema,
    capability: Type.String({ minLength: 1, maxLength: 160 }),
    requested_by: SubjectIdSchema,
    created_at: IsoDateTimeSchema,
    expires_at: IsoDateTimeSchema,
    resolved_at: Type.Optional(IsoDateTimeSchema),
    required_approvals: Type.Integer({ minimum: 1 }),
    // The digest a decision must echo: the run's server-computed action hash.
    expected_action_sha256: Sha256Schema,
    target: Type.Object({
        type: Type.Union([Type.Literal("run"), Type.Literal("job"), Type.Literal("artifact")]),
        id: Type.String({ minLength: 1, maxLength: 256 }),
        version: Type.String({ minLength: 1, maxLength: 256 }),
        sha256: Sha256Schema,
    }, { additionalProperties: false }),
    decisions: Type.Array(ApprovalDecisionRecordSchema),
    run: RunResponseSchema,
}, { additionalProperties: false, $id: "carbon.operations.approval/v1" });
export const WorkspaceOperatorSchema = Type.Object({
    operator_id: OpaqueIdSchema,
    display_name: Type.String({ minLength: 1, maxLength: 256 }),
    operator_type: Type.Union([Type.Literal("human"), Type.Literal("service"), Type.Literal("runner")]),
    operator_status: Type.Union([Type.Literal("active"), Type.Literal("disabled")]),
    role: WorkspaceRoleSchema,
    membership_status: Type.Union([Type.Literal("active"), Type.Literal("revoked")]),
    member_since: IsoDateTimeSchema,
    scopes: Type.Array(Type.String({ minLength: 1, maxLength: 160 })),
    capabilities: Type.Array(Type.Object({
        capability: Type.String({ minLength: 1, maxLength: 160 }),
        version: Type.Integer({ minimum: 1 }),
        title: Type.String({ minLength: 1, maxLength: 256 }),
        granted_at: IsoDateTimeSchema,
        granted_by: Type.String({ minLength: 1, maxLength: 256 }),
    }, { additionalProperties: false })),
}, { additionalProperties: false });
export const WorkspaceOperatorsResponseSchema = Type.Object({
    schema: Type.Literal("carbon.operations.workspace-operators/v1"),
    tenant_id: OpaqueIdSchema,
    workspace_id: OpaqueIdSchema,
    operators: Type.Array(WorkspaceOperatorSchema),
}, { additionalProperties: false, $id: "carbon.operations.workspace-operators/v1" });
// Custody records name a secret reference and its state. They never carry the secret's locator, version
// selector or value: secret_refs locators stay server-side.
export const SecretRefUsageSchema = Type.Union([
    Type.Literal("event_sink_signing"), Type.Literal("object_store_credential"),
    Type.Literal("artifact_encryption"), Type.Literal("runner_job"),
]);
export const WorkspaceSecretRefSchema = Type.Object({
    secret_ref_id: Type.String({ minLength: 1, maxLength: 256 }),
    provider: Type.String({ minLength: 1, maxLength: 160 }),
    purpose: Type.String({ minLength: 1, maxLength: 500 }),
    usages: Type.Array(SecretRefUsageSchema, { minItems: 1 }),
    status: Type.Union([Type.Literal("active"), Type.Literal("revoked")]),
    created_at: IsoDateTimeSchema,
    revoked_at: Type.Optional(IsoDateTimeSchema),
}, { additionalProperties: false });
export const WorkspaceEventSinkSchema = Type.Object({
    event_sink_id: OpaqueIdSchema,
    status: Type.Union([Type.Literal("active"), Type.Literal("disabled")]),
    // Origin only: a destination path or query can carry a routing token, so it is not returned.
    destination_origin: Type.String({ minLength: 9, maxLength: 300, pattern: "^https://[^/?#@]+$" }),
    key_id: Type.String({ minLength: 1, maxLength: 256 }),
    sink_version: Type.Integer({ minimum: 1 }),
    signing_key_ref_id: Type.Optional(Type.String({ minLength: 1, maxLength: 256 })),
    created_at: IsoDateTimeSchema,
}, { additionalProperties: false });
export const WorkspaceObjectStoreProfileSchema = Type.Object({
    profile_id: Type.String({ minLength: 1, maxLength: 256 }),
    profile_version: Type.Integer({ minimum: 1 }),
    provider: Type.Literal("s3"),
    bucket_ref: Type.String({ minLength: 1, maxLength: 512 }),
    credential_ref_id: Type.Optional(Type.String({ minLength: 1, maxLength: 256 })),
    configuration_sha256: Sha256Schema,
    status: Type.Union([Type.Literal("active"), Type.Literal("disabled")]),
    created_at: IsoDateTimeSchema,
}, { additionalProperties: false });
export const WorkspaceConnectionsResponseSchema = Type.Object({
    schema: Type.Literal("carbon.operations.workspace-connections/v1"),
    tenant_id: OpaqueIdSchema,
    workspace_id: OpaqueIdSchema,
    event_sinks: Type.Array(WorkspaceEventSinkSchema),
    object_store_profiles: Type.Array(WorkspaceObjectStoreProfileSchema),
    secret_refs: Type.Array(WorkspaceSecretRefSchema),
}, { additionalProperties: false, $id: "carbon.operations.workspace-connections/v1" });
const AuditListLimitSchema = Type.Optional(Type.String({ pattern: "^(100|[1-9][0-9]?)$" }));
export const AuditEventTypeFilterSchema = Type.String({ minLength: 1, maxLength: 100, pattern: "^[a-z][a-z0-9_]*(\\.[a-z0-9_]+)*$" });
export const AuditListQuerySchema = Type.Object({
    run_id: Type.Optional(OpaqueIdSchema),
    event_type: Type.Optional(AuditEventTypeFilterSchema),
    from: Type.Optional(IsoDateTimeSchema),
    to: Type.Optional(IsoDateTimeSchema),
    limit: AuditListLimitSchema,
    cursor: Type.Optional(ListCursorSchema),
}, { additionalProperties: false });
export const AuditEventRecordSchema = Type.Object({
    audit_sequence: Type.Integer({ minimum: 1 }),
    event_id: Type.String({ minLength: 1, maxLength: 256 }),
    event_type: Type.String({ minLength: 1, maxLength: 160 }),
    actor_type: Type.Union([Type.Literal("operator"), Type.Literal("runner"), Type.Literal("system")]),
    actor_id: Type.Optional(Type.String({ minLength: 1, maxLength: 256 })),
    request_id: Type.Optional(Type.String({ minLength: 1, maxLength: 256 })),
    subject_type: Type.String({ minLength: 1, maxLength: 64 }),
    subject_id: Type.String({ minLength: 1, maxLength: 256 }),
    run_id: Type.Optional(OpaqueIdSchema),
    occurred_at: IsoDateTimeSchema,
    payload: JsonObjectSchema,
    event_sha256: Sha256Schema,
    previous_sha256: Type.Optional(Sha256Schema),
    // hash: the stored event hash equals the hash recomputed from the stored fields.
    // link: previous_sha256 equals the stored hash of the tenant chain's preceding event (absent only at position 1).
    verification: Type.Object({ hash: Type.Boolean(), link: Type.Boolean() }, { additionalProperties: false }),
}, { additionalProperties: false });
export const AuditListResponseSchema = Type.Object({
    schema: Type.Literal("carbon.operations.audit-events/v1"),
    tenant_id: OpaqueIdSchema,
    workspace_id: OpaqueIdSchema,
    // The audit chain is tenant-wide; the head is read from app.audit_heads.
    chain: Type.Object({
        head_sequence: Type.Integer({ minimum: 0 }),
        head_sha256: Type.Optional(Sha256Schema),
        // The head's last hash is the stored hash of the event at head_sequence and nothing is recorded past it.
        head_matches: Type.Boolean(),
    }, { additionalProperties: false }),
    events: Type.Array(AuditEventRecordSchema),
    next_cursor: Type.Optional(ListCursorSchema),
}, { additionalProperties: false, $id: "carbon.operations.audit-events/v1" });
export const WorkspaceSettingsResponseSchema = Type.Object({
    schema: Type.Literal("carbon.operations.workspace-settings/v1"),
    tenant_id: OpaqueIdSchema,
    workspace_id: OpaqueIdSchema,
    slug: Type.String({ minLength: 1, maxLength: 256 }),
    display_name: Type.String({ minLength: 1, maxLength: 256 }),
    status: Type.Union([Type.Literal("active"), Type.Literal("archived")]),
    created_at: IsoDateTimeSchema,
    // The stored owner-configuration record. The live gate (checks, state, execution) is GET .../readiness.
    readiness: Type.Object({
        revision: Type.Integer({ minimum: 0 }),
        configured_at: Type.Optional(IsoDateTimeSchema),
        configured_by: Type.Optional(Type.String({ minLength: 1, maxLength: 256 })),
        last_verified_at: Type.Optional(IsoDateTimeSchema),
    }, { additionalProperties: false }),
    // The stored app.workspaces.policy object, exactly as persisted.
    policy: JsonObjectSchema,
    configuration: Type.Optional(WorkspaceReadinessConfigurationSchema),
}, { additionalProperties: false, $id: "carbon.operations.workspace-settings/v1" });
//# sourceMappingURL=workspace-records.js.map